import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  PhoneAuthProvider,
  signInWithCredential,
  createUserWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './config';
import { User, Customer } from '../types';
import { generateCustomerId } from '../utils/idGenerator';

export class PhoneAuthService {
  private static recaptchaVerifier: RecaptchaVerifier | null = null;
  private static confirmationResult: ConfirmationResult | null = null;

  // Initialize reCAPTCHA verifier
  static initializeRecaptcha(containerId: string): RecaptchaVerifier {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
    }

    this.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
      callback: () => {
        console.log('✅ reCAPTCHA solved');
      },
      'expired-callback': () => {
        console.log('⚠️ reCAPTCHA expired');
      }
    });

    return this.recaptchaVerifier;
  }

  // Generate development OTP based on current time (4 digits - HHMM)
  static generateDevOTP(): string {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return hours + minutes;
  }

  // Generate OTP for previous minute (to handle timing edge cases)
  static generatePreviousMinuteOTP(): string {
    const now = new Date();
    now.setMinutes(now.getMinutes() - 1);
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return hours + minutes;
  }

  // Create mock user for development
  static async createMockUser(userId: string, customerData: {
    name: string;
    phone: string;
    orderType: 'dine-in' | 'delivery';
    address?: string;
  }): Promise<User> {
    const customerId = customerData.phone.replace(/\D/g, '');
    
    const userData: User = {
      id: userId,
      email: `${customerId}@dev.customer`,
      name: customerData.name,
      role: 'customer',
      customerId: customerId,
      phone: customerData.phone,
      address: customerData.address || '',
      city: customerData.orderType === 'delivery' ? 'Delivery' : 'Dine-in',
      profilePicture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
    };

    console.log('🔧 Created mock user for development:', userData);
    return userData;
  }

  // Send OTP to phone number
  static async sendOTP(phoneNumber: string): Promise<{ success: boolean; error?: string; devOTP?: string }> {
    try {
      console.log('📱 Sending OTP to:', phoneNumber);

      // Development mode - bypass Firebase and use time-based OTP
      const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
      
      if (isDevelopment) {
        const devOTP = this.generateDevOTP();
        console.log('🔧 Development mode - OTP:', devOTP);
        console.log('✅ Development OTP generated successfully');
        return { success: true, devOTP };
      }

      // Production mode - use Firebase
      // Initialize reCAPTCHA if not already done
      if (!this.recaptchaVerifier) {
        // Create a temporary container for invisible reCAPTCHA
        const recaptchaContainer = document.createElement('div');
        recaptchaContainer.id = 'recaptcha-container';
        recaptchaContainer.style.display = 'none';
        document.body.appendChild(recaptchaContainer);
        
        this.initializeRecaptcha('recaptcha-container');
      }

      // Send verification code
      if (!this.recaptchaVerifier) {
        throw new Error('reCAPTCHA verifier not initialized');
      }
      
      this.confirmationResult = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        this.recaptchaVerifier
      );

      console.log('✅ OTP sent successfully');
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error sending OTP:', error);
      
      let errorMessage = 'Failed to send OTP';
      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Invalid phone number format';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later';
      } else if (error.code === 'auth/captcha-check-failed') {
        errorMessage = 'reCAPTCHA verification failed';
      }

      return { success: false, error: errorMessage };
    }
  }

  // Verify OTP and create/login customer
  static async verifyOTP(
    otp: string,
    customerData: {
      name: string;
      phone: string;
      orderType: 'dine-in' | 'delivery';
      address?: string;
    }
  ): Promise<{ success: boolean; user?: User; error?: string }> {
    try {
      console.log('🔐 Verifying OTP:', otp);

      // Development mode - verify time-based OTP
      const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
      
      if (isDevelopment) {
        const currentDevOTP = this.generateDevOTP();
        const previousMinuteOTP = this.generatePreviousMinuteOTP();
        
        // Allow current minute OTP or previous minute OTP (for timing edge cases)
        if (otp !== currentDevOTP && otp !== previousMinuteOTP) {
          console.log('❌ Invalid development OTP. Expected:', currentDevOTP, 'or', previousMinuteOTP, 'Got:', otp);
          return { success: false, error: `Invalid OTP. Use current time: ${currentDevOTP}` };
        }
        
        console.log('✅ Development OTP verified successfully');
        
        // Create a mock user for development
        const mockUserId = `dev_${customerData.phone.replace(/\D/g, '')}`;
        const userData = await this.createMockUser(mockUserId, customerData);
        return { success: true, user: userData };
      }

      // Production mode - use Firebase
      if (!this.confirmationResult) {
        return { success: false, error: 'No OTP request found. Please request OTP first.' };
      }

      // Verify the OTP
      const result = await this.confirmationResult.confirm(otp);
      const firebaseUser = result.user;
      
      console.log('✅ OTP verified successfully for user:', firebaseUser.uid);

      // Check if customer already exists
      const existingUserDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (existingUserDoc.exists()) {
        // Existing customer - return their data
        const userData = existingUserDoc.data() as User;
        console.log('👤 Existing customer found:', userData);
        return { success: true, user: userData };
      }

      // New customer - create their account
      console.log('👤 Creating new customer account...');
      
      // Generate unique customer ID using phone number
      const customerId = customerData.phone.replace(/\D/g, ''); // Remove non-digits
      
      // Update Firebase user profile
      await updateProfile(firebaseUser, {
        displayName: customerData.name
      });

      // Create user document
      const userData: User = {
        id: firebaseUser.uid,
        email: `${customerId}@customer.phone`, // Use phone as email identifier
        name: customerData.name,
        role: 'customer',
        customerId: customerId,
        phone: customerData.phone,
        address: customerData.address || '',
        city: customerData.orderType === 'delivery' ? 'Delivery' : 'Dine-in',
        profilePicture: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
      };

      // Save user data to Firestore
      await setDoc(doc(db, 'users', firebaseUser.uid), userData);
      console.log('✅ User document created');

      // Create customer document
      const customerDoc: Customer = {
        id: firebaseUser.uid,
        email: userData.email,
        name: customerData.name,
        phone: customerData.phone,
        address: customerData.address || '',
        city: userData.city || 'Unknown',
        customerId: customerId,
        profilePicture: userData.profilePicture,
        registrationDate: new Date().toISOString(),
        totalOrders: 0,
        totalSpent: 0
      };

      await setDoc(doc(db, 'customers', firebaseUser.uid), customerDoc);
      console.log('✅ Customer document created');

      return { success: true, user: userData };
    } catch (error: any) {
      console.error('❌ Error verifying OTP:', error);
      
      let errorMessage = 'Invalid OTP';
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = 'Invalid verification code';
      } else if (error.code === 'auth/code-expired') {
        errorMessage = 'Verification code has expired';
      }

      return { success: false, error: errorMessage };
    }
  }

  // Check if customer exists by phone number
  static async checkCustomerExists(phoneNumber: string): Promise<{ exists: boolean; user?: User }> {
    try {
      // Search for customer by phone number in customers collection
      // Note: In a production app, you'd want to create a phone number index
      // For now, we'll use the phone number as the customerId
      const customerId = phoneNumber.replace(/\D/g, '');
      
      // This is a simplified check - in production you'd query by phone field
      console.log('🔍 Checking if customer exists with phone:', phoneNumber);
      
      return { exists: false }; // For now, always treat as new customer
    } catch (error) {
      console.error('❌ Error checking customer existence:', error);
      return { exists: false };
    }
  }

  // Clean up reCAPTCHA
  static cleanup() {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
    }
    this.confirmationResult = null;
    
    // Remove reCAPTCHA container if it exists
    const container = document.getElementById('recaptcha-container');
    if (container) {
      container.remove();
    }
  }
}